import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Bell,
  CalendarDays,
  Plus,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import type { CalendarEvent, Paginated, Reminder } from "@worki/shared";
import { api, RequestError } from "../lib/api.js";
import { currentLocale } from "../i18n.js";
import { Button, ErrorNotice, Spinner } from "../components/ui.js";
import { ReminderDialog } from "../components/ReminderDialog.js";
import { CalendarSurface } from "../components/calendar/CalendarSurface.js";
import {
  calendarWindow,
  calendarFetchWindow,
  dateInputValue,
  moveCalendarDate,
  type CalendarView,
} from "../lib/calendarDates.js";
import { cn } from "../lib/cn.js";
export {
  calendarWindow,
  calendarFetchWindow,
  dateInputValue,
  moveCalendarDate,
} from "../lib/calendarDates.js";
const VIEWS = ["month", "week"] as const;

export function CalendarPage() {
  const { t } = useTranslation();
  const navigate = useNavigate(),
    queryClient = useQueryClient();
  const [view, setView] = useState<CalendarView>("month");
  const [anchor, setAnchor] = useState(() => new Date());
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const locale = currentLocale();
  const { from, to } = calendarFetchWindow(anchor, view);
  const fromISO = from.toISOString(),
    toISO = to.toISOString();
  const events = useInfiniteQuery({
    queryKey: ["calendar", fromISO, toISO],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api.get<Paginated<CalendarEvent>>(
        `/calendar/events?${new URLSearchParams({ from: fromISO, to: toISO, page: String(pageParam), pageSize: "100" })}`,
      ),
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  });
  const {
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = events;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isFetchNextPageError)
      void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);
  const reminder = useQuery({
    queryKey: ["reminder", editingId],
    queryFn: () =>
      api.get<Reminder>(`/reminders/${encodeURIComponent(editingId!)}`),
    enabled: editingId !== null,
  });
  const items = useMemo(
    () => events.data?.pages.flatMap((page) => page.items) ?? [],
    [events.data],
  );
  const selected = dateInputValue(anchor);
  const selectedItems = items
    .filter((event) => dateInputValue(new Date(event.start)) === selected)
    .sort((a, b) => a.start.localeCompare(b.start) || a.id.localeCompare(b.id));
  const fullDate = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const range = calendarWindow(anchor, view),
    last = new Date(range.to);
  last.setDate(last.getDate() - 1);
  const rangeLabel =
    view === "month"
      ? new Intl.DateTimeFormat(locale, {
          month: "long",
          year: "numeric",
        }).format(anchor)
      : view === "day"
        ? fullDate.format(anchor)
        : `${fullDate.format(range.from)} – ${fullDate.format(last)}`;
  const openEvent = useCallback(
    (event: CalendarEvent) => {
      if (event.kind === "meeting")
        navigate(`/inquiries?open=${encodeURIComponent(event.sourceId)}`);
      else {
        setCreating(false);
        setEditingId(event.sourceId);
      }
    },
    [navigate],
  );
  const closeEditor = () => {
    setCreating(false);
    setEditingId(null);
    void queryClient.invalidateQueries({ queryKey: ["calendar"] });
  };
  const renderEvent = (event: CalendarEvent) => {
    const content = (
      <>
        <span
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
            event.kind === "meeting"
              ? "bg-info-soft text-info"
              : "bg-primary/10 text-primary",
          )}
        >
          {event.kind === "meeting" ? (
            <CalendarDays className="h-4 w-4" />
          ) : (
            <Bell className="h-4 w-4" />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block break-words font-medium">{event.title}</span>
          <span className="text-xs text-muted-foreground">
            {t(
              event.kind === "meeting"
                ? "calendar.meetingKind"
                : "calendar.reminderKind",
            )}
          </span>
        </span>
        <time
          dateTime={event.start}
          className="shrink-0 text-sm font-medium tabular-nums"
        >
          {new Intl.DateTimeFormat(locale, {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }).format(new Date(event.start))}
        </time>
      </>
    );
    const className =
      "flex min-h-16 w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-start hover:border-primary/50";
    return (
      <li key={event.id}>
        {event.kind === "meeting" ? (
          <Link
            className={className}
            to={`/inquiries?open=${encodeURIComponent(event.sourceId)}`}
            aria-label={`${t("calendar.openMeeting")}: ${event.title}`}
          >
            {content}
          </Link>
        ) : (
          <button
            type="button"
            className={className}
            aria-label={`${t("calendar.openReminder")}: ${event.title}`}
            onClick={() => openEvent(event)}
          >
            {content}
          </button>
        )}
      </li>
    );
  };
  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-xl font-bold">
          <CalendarDays className="h-5 w-5 text-primary" />
          <span aria-live="polite"><span className="sr-only">{t('calendar.selectedDay')}: </span>{new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(anchor)}</span>
        </h1>
        <Button
          variant="primary"
          icon={<Plus className="h-4 w-4" />}
          onClick={() => {
            setEditingId(null);
            setCreating(true);
          }}
        >
          {t("reminders.new")}
        </Button>
      </div>
      <div className={cn('grid gap-5 lg:items-start', locale === 'he' ? 'lg:grid-cols-[minmax(0,1fr)_20rem]' : 'lg:grid-cols-[20rem_minmax(0,1fr)]')}>
      <section className="overflow-hidden rounded-2xl border border-border bg-card"
        onTouchStart={event => { const point = event.touches[0]; touchStart.current = point ? { x: point.clientX, y: point.clientY } : null; }}
        onTouchEnd={event => {
          const start = touchStart.current, point = event.changedTouches[0]; touchStart.current = null;
          if (!start || !point || event.touches.length) return;
          const dx = point.clientX - start.x, dy = point.clientY - start.y;
          if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 2) {
            const direction = (dx > 0) === (locale === 'he') ? 1 : -1;
            setAnchor(date => moveCalendarDate(date, view, direction));
          }
        }}>
        <div className="space-y-3 border-b border-border p-3 sm:p-5">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base font-bold sm:text-xl" aria-live="polite">
              {rangeLabel}
            </h2>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div
              className="flex gap-1 rounded-xl bg-muted p-1"
              role="group"
              aria-label={t("calendar.view")}
            >
              {VIEWS.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={view === option}
                  onClick={() => setView(option)}
                  className={cn(
                    "min-h-11 rounded-lg px-3 sm:px-4 text-sm font-medium transition-colors",
                    view === option
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-accent",
                  )}
                >
                  {t(`calendar.${option}`)}
                </button>
              ))}
            </div>
            {view !== 'month' && <div className="flex gap-1">
              <button type="button" className="min-h-11 min-w-11 grid place-items-center rounded-lg hover:bg-muted" aria-label={t('completion.previous')} onClick={() => setAnchor(date => moveCalendarDate(date, view, -1))}><ChevronLeft className="h-5 w-5 rtl:rotate-180" /></button>
              <button type="button" className="min-h-11 min-w-11 grid place-items-center rounded-lg hover:bg-muted" aria-label={t('completion.next')} onClick={() => setAnchor(date => moveCalendarDate(date, view, 1))}><ChevronRight className="h-5 w-5 rtl:rotate-180" /></button>
            </div>}
            <div className="flex gap-2" aria-label={t('completion.calendarMonth')}>
              <select aria-label={t('completion.month')} className="min-h-11 rounded-lg border border-border bg-background px-2 text-sm" value={anchor.getMonth()} onChange={event => setAnchor(new Date(anchor.getFullYear(), Number(event.target.value), 1))}>
                {Array.from({ length: 12 }, (_, month) => <option key={month} value={month}>{new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2026, month, 1))}</option>)}
              </select>
              <select aria-label={t('completion.year')} className="min-h-11 rounded-lg border border-border bg-background px-2 text-sm" value={anchor.getFullYear()} onChange={event => setAnchor(new Date(Number(event.target.value), anchor.getMonth(), 1))}>
                {Array.from({ length: 41 }, (_, index) => anchor.getFullYear() - 20 + index).map(year => <option key={year} value={year}>{year}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Bell className="h-3 w-3 text-primary" />
              {t("calendar.reminderKind")}
            </span>
            <span className="flex items-center gap-1.5">
              <CalendarDays className="h-3 w-3 text-info" />
              {t("calendar.meetingKind")}
            </span>
          </div>
        </div>
        <div className="p-1 sm:p-4" aria-busy={events.isFetching}>
          <CalendarSurface
            view={view}
            date={anchor}
            events={items}
            locale={locale}
            onDateSelect={setAnchor}
            onDateCreate={date => {
              setAnchor(date);
              setEditingId(null);
              setCreating(true);
            }}
            onEventOpen={openEvent}
          />
        </div>
      </section>
      {events.isFetching && (
        <p
          role="status"
          className="flex items-center gap-2 text-sm text-muted-foreground"
        >
          <Spinner />
          {t("common.loading")}
        </p>
      )}
      {events.isError && (
        <div className="space-y-2">
          <ErrorNotice
            code={
              events.error instanceof RequestError
                ? events.error.code
                : "errors.unknown"
            }
          />
          <Button
            onClick={() =>
              void (isFetchNextPageError
                ? events.fetchNextPage()
                : events.refetch())
            }
          >
            {t("common.retry")}
          </Button>
        </div>
      )}
      <section aria-label={t("calendar.selectedDay")} className={cn('space-y-3 lg:row-start-1', locale === 'he' ? 'lg:col-start-2' : 'lg:col-start-1')}>
        <div className="flex items-center justify-between">
          <h2 className="font-semibold" aria-live="polite">
            {fullDate.format(anchor)}
          </h2>
          <span className="text-xs text-muted-foreground">
            {t("calendar.eventsCount", { count: selectedItems.length })}
          </span>
        </div>
        <ul className="space-y-2">{selectedItems.map(renderEvent)}</ul>
        {events.isSuccess &&
          !events.hasNextPage &&
          selectedItems.length === 0 && (
            <p
              role="status"
              className="rounded-xl border border-border bg-card p-5 text-center text-sm text-muted-foreground"
            >
              {t("calendar.emptyDay")}
            </p>
          )}
      </section>
      </div>
      {events.hasNextPage && (
        <Button
          block
          loading={events.isFetchingNextPage}
          onClick={() => void events.fetchNextPage()}
        >
          {t("calendar.loadMore")}
        </Button>
      )}
      {editingId && reminder.isPending && (
        <p role="status" className="flex items-center gap-2">
          <Spinner />
          {t("common.loading")}
        </p>
      )}
      {editingId && reminder.isError && (
        <div className="space-y-2">
          <ErrorNotice
            code={
              reminder.error instanceof RequestError
                ? reminder.error.code
                : "errors.unknown"
            }
          />
          <Button onClick={() => void reminder.refetch()}>
            {t("common.retry")}
          </Button>
          <Button variant="ghost" onClick={() => setEditingId(null)}>
            {t("common.cancel")}
          </Button>
        </div>
      )}
      <ReminderDialog
        open={creating || Boolean(editingId && reminder.data)}
        reminder={editingId ? reminder.data : null}
        defaultDueAt={new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate(), 9).toISOString()}
        onClose={closeEditor}
      />
    </div>
  );
}
